import { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Says the record being typed already exists and offers to open it instead of
 * creating a duplicate. Also the "still checking" line, so the user is never
 * left wondering why Next is not available yet.
 *
 * `role="status"` rather than `alert`: it appears while the user is still typing,
 * and interrupting them for it would be worse than the duplicate.
 */
function ExistingRecordNotice({ entityType, matches, checking, domainRoot }) {
  const { t } = useTranslation('unified-profiles');

  if (matches.length > 0) {
    return (
      <div
        role="status"
        className="flex flex-col gap-2 rounded-lg border border-amber-400/40 bg-amber-500/10 p-4"
      >
        <p className="flex items-center gap-2 text-sm font-medium text-content">
          <AlertTriangle className="size-4 shrink-0 text-amber-500" aria-hidden />
          {t(`create.exists.${entityType}`)}
        </p>
        <ul className="flex flex-col gap-1">
          {matches.map((match) => (
            <li key={`${match.entityType ?? entityType}:${match.id}`} className="text-sm">
              <Link
                to={`${domainRoot}/${match.entityType ?? entityType}/${match.id}`}
                className="font-medium text-accent hover:underline"
              >
                {t('create.exists.open')} · {match.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (checking) {
    return (
      <p role="status" className="flex items-center gap-2 text-xs text-content-muted">
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        {t('create.exists.checking')}
      </p>
    );
  }

  return null;
}

export default memo(ExistingRecordNotice);
